import { ButtonIcon, Flex, ProgressBar, Typography } from 'components';
import { useEffect, useState } from 'react';
import styled, { ColorsTypes } from 'styled-components';

import { MilestoneProps } from './types';

const Wrapper = styled.div(({ theme }) => {
  return `
    display: flex;
    flex-direction: column;
    background: ${theme.colors.dark_350};
    padding-top: ${theme.space[4]}px;
    padding-right: ${theme.space[4]}px;
    border-radius: 4px;
    position: relative;
  `;
});

const Square = styled.div(({ theme }) => {
  return `
    width: 12px;
    height: 12px;
    background: ${theme.colors.primary_400};
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  `;
});

const Bullet = styled.div((props: any) => {
  return `
    width: 24px;
    height: 24px;
    border-radius: 12px;
    background: ${props.theme.colors.dark_350};
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: -5px;

    &::before {
      content: '';
      width: 12px;
      height: 12px;
      border-radius: 6px;
      background: ${props.theme.colors[(props['data-color'] as ColorsTypes) || 'primary_400']};
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  `;
});

const Line = styled.div(({ theme, ...props }) => {
  return `
  position: relative;
  width: 60px;
  overflow: hidden;
  flex-shrink: 0;

  &::before {
    content: '';
    display: ${(props as any)['data-last'] ? 'none' : 'block'};
    width: 2px;
    height: 100%;
    background: ${theme.colors.gray_600};
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
`;
});

const CollapseIcon = styled(ButtonIcon)(({ theme }) => {
  return `
    position: absolute;
    right: 15px;
    top: 15px;
  `;
});

const Truncate = styled.div`
  display: table;
  table-layout: fixed;
  width: 100%;

  div {
    display: table-cell;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const Milestone = ({ stones, collapse: receivedCollapse = false, ...rest }: MilestoneProps) => {
  const [collapsed, setCollapsed] = useState(receivedCollapse);

  useEffect(() => {
    setCollapsed(receivedCollapse);
  }, [receivedCollapse]);

  return (
    <Wrapper data-milestone>
      <CollapseIcon
        icon={collapsed ? 'chevronUp' : 'chevronDown'}
        bg="transparent"
        color="primary_400"
        iconWidth={22}
        onClick={() => setCollapsed(!collapsed)}
      />

      <Flex flexWrap="nowrap">
        <Line>
          <Square />
        </Line>

        <Flex flexDirection="column" justifyContent="flex-start" pb={collapsed ? 6 : 3}>
          <Typography type="type1" color="gray_500" upper>
            {rest.hat}
          </Typography>

          <Typography type="type3" color="white" fontWeight="bold" upper mt={2}>
            {rest.title}
          </Typography>

          <Flex mt={3} flexWrap="nowrap" alignItems="center">
            {!!rest.progressLabel && (
              <Typography type="type1" color={rest.progressBarPercentageColor} mr={3}>
                {rest.progressLabel}
              </Typography>
            )}

            <ProgressBar
              height={8}
              percent={rest.progressBarPercentage}
              bulletPercent={rest.progressBulletPercentage}
              bgBar={rest.progressBarPercentageColor}
              bgTrack="dark_150"
            />
          </Flex>
        </Flex>
      </Flex>

      {collapsed && (
        <Flex flexDirection="column">
          {stones?.map((stone, index) => {
            return (
              <Flex
                key={`milestone-item-${index.toString()}`}
                onClick={() => {
                  if (stone.onClick) stone.onClick();
                }}
                flexWrap="nowrap"
                style={{ cursor: stone.onClick ? 'pointer' : 'inherit' }}
              >
                <Line data-last={index === stones?.length - 1}>
                  <Bullet data-color={stone.color} />
                </Line>

                <Flex flexDirection="column" justifyContent="flex-start" pb={3} position="relative">
                  <Truncate>
                    <Typography type="type1" color={stone.isQuestionInvalidated ? 'warning_200' : 'gray_300'}>
                      {stone.content}
                    </Typography>
                  </Truncate>
                </Flex>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Wrapper>
  );
};

export default Milestone;
